import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { GuiaMateriaPrima, GuiaMateriaPrimaState, EstadoGuia } from '../types';
import { GuiaMateriaPrimaService } from '../services/GuiaMateriaPrimaService';
import { errorService } from '../../../../services/errorService';

interface GuiaMateriaPrimaActions {
  fetchGuias: (page?: number, pageSize?: number) => Promise<void>;
  fetchGuiaById: (id: number) => Promise<GuiaMateriaPrima | null>;
  createGuia: (data: any) => Promise<GuiaMateriaPrima | null>;
  updateGuia: (id: number, data: any) => Promise<GuiaMateriaPrima | null>;
  deleteGuia: (id: number) => Promise<boolean>;
  updateEstado: (id: number, estado: EstadoGuia) => Promise<boolean>;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  clearError: () => void;
  reset: () => void;
}

type GuiaMateriaPrimaStore = GuiaMateriaPrimaState & GuiaMateriaPrimaActions;

const initialState: GuiaMateriaPrimaState = {
  guias: [],
  loading: false,
  error: null,
  totalCount: 0,
  currentPage: 1,
  pageSize: 10,
};

export const useGuiaMateriaPrimaStore = create<GuiaMateriaPrimaStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      fetchGuias: async (page, pageSize) => {
        const { currentPage, pageSize: currentPageSize } = get();
        const targetPage = page ?? currentPage;
        const targetPageSize = pageSize ?? currentPageSize;

        set({ loading: true, error: null });

        try {
          const response = await GuiaMateriaPrimaService.getAll(targetPage, targetPageSize);
          set({
            guias: response.data,
            totalCount: response.totalCount,
            currentPage: targetPage,
            pageSize: targetPageSize,
            loading: false,
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Error al cargar las guías',
            loading: false,
          });
        }
      },

      fetchGuiaById: async (id: number) => {
        set({ loading: true, error: null });

        try {
          const guia = await GuiaMateriaPrimaService.getById(id);
          set({ loading: false });
          return guia;
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Error al cargar la guía',
            loading: false,
          });
          return null;
        }
      },

      createGuia: async (data) => {
        set({ loading: true, error: null });

        try {
          const newGuia = await GuiaMateriaPrimaService.create(data);
          const { fetchGuias } = get();
          await fetchGuias();
          set({ loading: false });
          errorService.showSuccess('Guía de materia prima creada exitosamente');
          return newGuia;
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Error al crear la guía',
            loading: false,
          });
          return null;
        }
      },

      updateGuia: async (id: number, data) => {
        set({ loading: true, error: null });

        try {
          const updatedGuia = await GuiaMateriaPrimaService.update(id, data);
          const { fetchGuias } = get();
          await fetchGuias();
          set({ loading: false });
          errorService.showSuccess('Guía de materia prima actualizada exitosamente');
          return updatedGuia;
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Error al actualizar la guía',
            loading: false,
          });
          return null;
        }
      },

      deleteGuia: async (id: number) => {
        set({ loading: true, error: null });

        try {
          await GuiaMateriaPrimaService.delete(id);
          const { fetchGuias } = get();
          await fetchGuias();
          set({ loading: false });
          return true;
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Error al eliminar la guía',
            loading: false,
          });
          return false;
        }
      },

      updateEstado: async (id: number, estado: EstadoGuia) => {
        set({ loading: true, error: null });

        try {
          await GuiaMateriaPrimaService.updateEstado(id, estado);
          const { fetchGuias } = get();
          await fetchGuias();
          set({ loading: false });
          return true;
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Error al actualizar el estado',
            loading: false,
          });
          return false;
        }
      },

      setPage: (page: number) => {
        set({ currentPage: page });
      },

      setPageSize: (pageSize: number) => {
        set({ pageSize, currentPage: 1 });
      },

      clearError: () => {
        set({ error: null });
      },

      reset: () => {
        set(initialState);
      },
    }),
    { name: 'guia-materia-prima-store' }
  )
);